import { ApiCore } from "./core";
import { apiProvider } from "./core/provider";

const url = "reports";

const apiReports = new ApiCore({
  list: false,
  get: false,
  post: false,
  put: false,
  patch: false,
  delete: false,
  url: url,
});

apiReports.getZipCodeHeatMap = function(from, to) {
  const fromTimestamp = new Date(from).getTime();
  const toTimestamp = new Date(to).getTime();
  return apiProvider.get(`${url}/zipCodeHeatMap?from=${fromTimestamp}&to=${toTimestamp}`);
};

apiReports.getIncomingMap = function() {
  return apiProvider.get(`${url}/incoming-map`);
};

apiReports.getIncomingMonthlyTrend = function() {
  return apiProvider.get(`${url}/incoming-monthly-trend`);
};

apiReports.getPipeline = function() {
  return apiProvider.get(`${url}/pipeline`);
};

apiReports.getPenaltyRisk = function() {
  return apiProvider.get(`${url}/penaltyrisk`);
};

apiReports.claimPenaltyRisk = function(inspectionId, noAction = false) {
  return apiProvider.post(`${url}/penaltyrisk/claim`, { inspectionId, noAction });
};

apiReports.releasePenaltyRisk = function(inspectionId) {
  return apiProvider.post(`${url}/penaltyrisk/release`, { inspectionId });
};

apiReports.setPenaltyRiskClaimState = function(inspectionId, noAction) {
  return apiProvider.post(`${url}/penaltyrisk/claimstate`, { inspectionId, noAction });
};

apiReports.getMetricHistory = function(metric, days = 30) {
  return apiProvider.get(`${url}/metric-history?metric=${encodeURIComponent(metric)}&days=${days}`);
};

apiReports.getNewAreas = function() {
  return apiProvider.get(`${url}/new-areas`);
};

apiReports.getInspectorCoverage = function(minutes = 60) {
  return apiProvider.get(`${url}/inspector-coverage?minutes=${encodeURIComponent(minutes)}`);
};

apiReports.getInspectorUtilisation = function(days = 30) {
  return apiProvider.get(`${url}/inspector-utilisation?days=${encodeURIComponent(days)}`);
};

apiReports.getInspectorUtilisationDetail = function(userId, days = 30) {
  return apiProvider.get(`${url}/inspector-utilisation/${encodeURIComponent(userId)}?days=${encodeURIComponent(days)}`);
};

apiReports.getAreaUtilisationDetail = function(workAreaId, days = 30) {
  return apiProvider.get(`${url}/area-utilisation/${encodeURIComponent(workAreaId)}?days=${encodeURIComponent(days)}`);
};

apiReports.generateNaturalLanguageReport = function(prompt) {
  return apiProvider.post(`${url}/natural-language`, { prompt });
};

apiReports.getSavedNaturalLanguageReports = function() {
  return apiProvider.get(`${url}/natural-language/saved`);
};

apiReports.saveNaturalLanguageReport = function(report) {
  return apiProvider.post(`${url}/natural-language/saved`, report);
};

apiReports.runSavedNaturalLanguageReport = function(id) {
  return apiProvider.post(`${url}/natural-language/saved/${encodeURIComponent(id)}/run`);
};

apiReports.getOutOfArea = function(thresholdMinutes = 0) {
  return apiProvider.get(`${url}/out-of-area?thresh=${thresholdMinutes}`);
};

apiReports.spotCheckOutOfArea = function(lat, lng, thresholdMinutes = 0) {
  return apiProvider.get(`${url}/out-of-area/spot?lat=${lat}&lng=${lng}&thresh=${thresholdMinutes}`);
};

apiReports.getOutOfAreaBatches = function() {
  return apiProvider.get(`${url}/out-of-area/batches`);
};

apiReports.createOutOfAreaBatch = function(policyNos) {
  return apiProvider.post(`${url}/out-of-area/batches`, { policyNos });
};

apiReports.returnOutOfAreaBatch = function(id, returnedAt) {
  return apiProvider.post(`${url}/out-of-area/batches/${id}/return`, { returnedAt });
};

apiReports.getHealthMap = function(days = 30) {
  return apiProvider.get(`${url}/health-map?days=${encodeURIComponent(days)}`);
};

apiReports.getCompletedStats = function() {
  return apiProvider.get(`${url}/completed`);
};


apiReports.getSchedulingAnalytics = function(from, to) {
  return apiProvider.get(`${url}/scheduling-analytics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
};

apiReports.getTimeMonitoring = function(from, to) {
  return apiProvider.get(`${url}/time-monitoring?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
};

apiReports.getTimeMonitoringActivity = function(userId, date) {
  return apiProvider.get(`${url}/time-monitoring/${encodeURIComponent(userId)}/activity?date=${encodeURIComponent(date)}`);
};

apiReports.getTimeMonitoringNarrative = function(userId, date) {
  return apiProvider.get(`${url}/time-monitoring/${encodeURIComponent(userId)}/narrative?date=${encodeURIComponent(date)}`);
};

apiReports.generateTimeMonitoringNarrative = function(userId, date) {
  return apiProvider.post(`${url}/time-monitoring/${encodeURIComponent(userId)}/narrative`, { day: date });
};

apiReports.getTimeMonitoringScores = function(date) {
  return apiProvider.get(`${url}/time-monitoring/scores?date=${encodeURIComponent(date)}`);
};

apiReports.regenerateTimeMonitoringDay = function(date) {
  return apiProvider.post(`${url}/time-monitoring/narratives/regenerate`, { day: date });
};

apiReports.getTimeMonitoringTeamNarrative = function(date) {
  return apiProvider.get(`${url}/time-monitoring/team-narrative?date=${encodeURIComponent(date)}`);
};

apiReports.generateTimeMonitoringTeamNarrative = function(date) {
  return apiProvider.post(`${url}/time-monitoring/team-narrative`, { day: date });
};

apiReports.getTimeMonitoringVerdict = function(userId, date) {
  return apiProvider.get(`${url}/time-monitoring/${encodeURIComponent(userId)}/verdict?date=${encodeURIComponent(date)}`);
};

apiReports.saveTimeMonitoringVerdict = function(userId, date, verdict, note) {
  return apiProvider.post(`${url}/time-monitoring/${encodeURIComponent(userId)}/verdict`, { day: date, verdict, note: note || "" });
};

apiReports.deleteTimeMonitoringVerdict = function(userId, date) {
  return apiProvider.delete(`${url}/time-monitoring/${encodeURIComponent(userId)}/verdict`, date);
};

apiReports.getSchedulerActivityToday = function(userId) {
  const person = userId == null ? "" : `/${encodeURIComponent(userId)}`;
  return apiProvider.get(`${url}/scheduler-utilisation${person}?today=1&days=1`).then((response) => {
    const result = response.data.result;
    const days = userId == null ? result?.summary?.days : result?.windowDays;
    if (response.data.status === 200 && days !== 1) {
      throw new Error("Expected today's scheduler activity, received a different reporting period.");
    }
    return response;
  });
};

apiReports.getSchedulerActivityDay = function(userId, date) {
  return apiProvider.get(`${url}/scheduler-utilisation/${encodeURIComponent(userId)}?today=1&days=1&date=${encodeURIComponent(date)}`).then((response) => {
    const result = response.data.result;
    if (response.data.status === 200 && (result?.windowDays !== 1 || result?.day !== date)) {
      throw new Error("Expected the selected day's scheduler activity, received a different reporting period.");
    }
    return response;
  });
};

export { apiReports };
